import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Terminal } from '@xterm/xterm';
import { getCookie } from '../utils/cookies';
import { getSocketUrl, BASE_PATH, authTenantHeaders } from '../services/api';
import { X } from 'lucide-react';
import { Button, IconButton, MonoCell } from '@/components/ds-zta';
import TerminalSurface, { type TerminalSize } from '@/components/terminal/TerminalSurface';
import TerminalChrome from '@/components/terminal/TerminalChrome';
import { connectionStatus } from '@/components/terminal/connectionStatus';

export default function K8sTerminalPage() {
    const xtermRef = useRef<Terminal | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [searchParams] = useSearchParams();

    const cluster = searchParams.get('cluster') || 'unknown';

    // Step 2: Connect WebSocket DIRECTLY to hub (same pattern as SSH terminal)
    const connectToWebSocket = useCallback((sessionId: string, attempt: number = 1) => {
        const wsUrl = `${getSocketUrl()}/agent/client/${encodeURIComponent(sessionId)}/ws`;

        if (xtermRef.current) {
            xtermRef.current.write(`\r\n\x1b[1;36mConnecting to cluster (attempt ${attempt})...\x1b[0m\r\n`);
        }

        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
            if (xtermRef.current) {
                xtermRef.current.write('\r\n\x1b[1;32mConnected\x1b[0m\r\n\r\n');
            }
            setIsConnected(true);
            setIsConnecting(false);
            document.title = `K8s Terminal — ${cluster}`;

            // Application-level heartbeat so the hub can detect a closed tab and finalize
            // the recording (WS ping/pong can be forged by proxies; this data can't).
            if (heartbeatRef.current) clearInterval(heartbeatRef.current);
            heartbeatRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) ws.send('{"type":"client_ping"}');
            }, 10000);
            // Focus terminal and send initial resize
            setTimeout(() => {
                xtermRef.current?.focus();
                if (xtermRef.current && ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({
                        type: 'resize',
                        cols: xtermRef.current.cols,
                        rows: xtermRef.current.rows,
                    }));
                }
            }, 500);
        };

        // Same message handler as SSH terminal — handles binary + text
        ws.onmessage = async (event) => {
            try {
                let data: string | ArrayBuffer;
                if (event.data instanceof Blob) {
                    data = await event.data.arrayBuffer();
                } else if (event.data instanceof ArrayBuffer) {
                    data = event.data;
                } else {
                    data = event.data;
                }

                let text: string;
                if (data instanceof ArrayBuffer) {
                    text = new TextDecoder().decode(data);
                } else {
                    text = data.toString();
                }

                if (xtermRef.current && text) {
                    xtermRef.current.write(text);
                }
            } catch (err) {
                console.error('Error processing K8s terminal message:', err);
            }
        };

        ws.onclose = () => {
            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
            if (xtermRef.current && isConnected) {
                xtermRef.current.write('\r\n\r\n\x1b[1;31mConnection closed\x1b[0m\r\n');
            }
            setIsConnected(false);
            setIsConnecting(false);
            wsRef.current = null;
        };

        ws.onerror = () => {
            setIsConnecting(false);
            if (attempt < 5) {
                setTimeout(() => {
                    connectToWebSocket(sessionId, attempt + 1);
                }, 2000);
            } else {
                if (xtermRef.current) {
                    xtermRef.current.write('\r\n\x1b[1;31mConnection failed after multiple attempts\x1b[0m\r\n');
                }
            }
        };
    }, [isConnected, cluster]);

    // Step 1: Create K8s terminal session via Agent Hub internal API
    const createSession = useCallback(async (attempt: number = 1) => {
        setIsConnecting(true);

        if (xtermRef.current) {
            if (attempt === 1) {
                xtermRef.current.clear();
            }
            xtermRef.current.write(`\x1b[1;36m⏳ Connecting to K8s cluster: ${cluster}...\x1b[0m\r\n`);
        }

        try {
            const token = getCookie('auth_token');
            if (!token) {
                if (xtermRef.current) {
                    xtermRef.current.write('\r\n\x1b[1;31mNo auth token found. Please login first.\x1b[0m\r\n');
                }
                setIsConnecting(false);
                return;
            }

            // Create session via admin-api (which calls agent-hub internally)
            const baseUrl = BASE_PATH;
            const response = await fetch(
                `${baseUrl}/api/v1/k8s/${encodeURIComponent(cluster)}/terminal/session`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authTenantHeaders(),
                    },
                    body: JSON.stringify({
                        cols: xtermRef.current?.cols || 120,
                        rows: xtermRef.current?.rows || 40,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                // A denial comes back as {error, message} — the message carries the
                // actual reason, so show it rather than a bare "Access denied".
                const detail = [errorData.error, errorData.message].filter(Boolean).join(' — ');
                const err: any = new Error(detail || `HTTP ${response.status}`);
                // An authorization failure doesn't heal by itself; retrying buries it.
                // 404/502/503 stay retryable — those appear while an agent reconnects.
                err.fatal = [401, 403].includes(response.status);
                throw err;
            }

            const data = await response.json();
            const sessionId = data.session_id;
            sessionIdRef.current = sessionId;

            // Connect WebSocket directly to hub (same as SSH terminal)
            connectToWebSocket(sessionId, 1);
        } catch (error: any) {
            const errorMessage = error?.message || 'Unknown error';
            if (xtermRef.current) {
                xtermRef.current.write(`\r\n\x1b[1;31mSession error: ${errorMessage}\x1b[0m\r\n`);
            }

            if (!error?.fatal && attempt < 3) {
                if (xtermRef.current) {
                    xtermRef.current.write(`\x1b[90mRetrying in 2 seconds...\x1b[0m\r\n`);
                }
                setTimeout(() => createSession(attempt + 1), 2000);
            } else {
                setIsConnecting(false);
                if (xtermRef.current && !error?.fatal) {
                    xtermRef.current.write('\r\n\x1b[1;31mFailed after multiple attempts\x1b[0m\r\n');
                }
            }
        }
    }, [cluster, connectToWebSocket]);

    // Handle kill connection
    const handleKillConnection = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
        }

        if (sessionIdRef.current) {
            const token = getCookie('auth_token');
            const baseUrl = BASE_PATH;
            fetch(`${baseUrl}/agent/ssh/${sessionIdRef.current}`, {
                method: 'DELETE',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).catch(() => { });
        }

        if (xtermRef.current) {
            xtermRef.current.clear();
        }

        sessionIdRef.current = null;
        setIsConnected(false);
        setIsConnecting(false);
        // Re-create session
        setTimeout(() => createSession(1), 500);
    }, [createSession]);

    // Fire-and-forget terminate that survives page unload (keepalive:true), so closing
    // the tab reliably finalizes the recording server-side (stop + save recording_url →
    // "Play") instead of leaving the session "active". The K8s web terminal streams
    // through the hub's client WebSocket, so DELETE /agent/ssh/:id (the hub terminate)
    // is the right endpoint — it closes the pump and runs the recording-finalize cleanup.
    const terminateSession = useCallback(() => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        // navigator.sendBeacon is the ONE request browsers guarantee to flush during
        // page unload (tab close), so it's the reliable finalize signal. Fall back to a
        // keepalive fetch if sendBeacon is unavailable or rejects.
        let sent = false;
        try {
            if (navigator.sendBeacon) sent = navigator.sendBeacon(`${BASE_PATH}/agent/session/${sid}/terminate`);
        } catch { /* ignore */ }
        if (!sent) {
            const token = getCookie('auth_token');
            try {
                fetch(`${BASE_PATH}/agent/ssh/${sid}`, {
                    method: 'DELETE',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                    keepalive: true,
                }).catch(() => { });
            } catch { /* ignore */ }
        }
    }, []);

    // Terminate on real tab/window close (not on tab-switch).
    useEffect(() => {
        const onUnload = () => terminateSession();
        window.addEventListener('pagehide', onUnload);
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('pagehide', onUnload);
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [terminateSession]);

    // The screen is a TerminalSurface (design eval F40): it owns the xterm
    // instance, fit and resize. This page owns the connection.
    const createSessionRef = useRef(createSession);
    createSessionRef.current = createSession;

    const handleReady = useCallback((term: Terminal) => {
        xtermRef.current = term;
        // Auto-connect on load
        createSessionRef.current(1);
    }, []);

    // Pipe terminal input → WebSocket (same as SSH terminal)
    const handleData = useCallback((data: string) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(data);
        }
    }, []);

    const handleResize = useCallback(({ cols, rows }: TerminalSize) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
    }, []);

    // Connection teardown when the cluster changes or the page unmounts.
    useEffect(() => {
        return () => {
            // Finalize the recording on unmount too (covers in-tab teardown).
            terminateSession();
            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [cluster, terminateSession]);

    const status = connectionStatus(isConnected, isConnecting);

    return (
        // A full-screen route outside the app shell: its own <main> and h1.
        <TerminalChrome
            as="main"
            headingLevel={1}
            className="h-screen w-screen"
            title="kubectl"
            target={cluster}
            status={status}
            actions={
                <>
                    {isConnected && (
                        <Button variant="danger" size="sm" onClick={handleKillConnection}>
                            Kill & Restart
                        </Button>
                    )}
                    <IconButton
                        aria-label="Close"
                        size="sm"
                        icon={<X />}
                        onClick={() => { terminateSession(); window.close(); }}
                    />
                </>
            }
            // The connection state is the header's status chip, once (design
            // eval F47: a footer "Ready" beside a header "Disconnected").
            footer={
                <div className="flex items-center gap-4">
                    <span className="font-mono">{cluster}</span>
                    {sessionIdRef.current && (
                        <span className="flex min-w-0 items-center gap-1">
                            session: <MonoCell value={sessionIdRef.current} width="6rem" />
                        </span>
                    )}
                </div>
            }
        >
            <TerminalSurface
                key={cluster}
                className="flex-1 p-1"
                fontSize={15}
                aria-label={`kubectl terminal for ${cluster}`}
                onReady={handleReady}
                onDispose={() => { xtermRef.current = null; }}
                onData={handleData}
                onResize={handleResize}
            />
        </TerminalChrome>
    );
}
